"use client";

import React, { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { getStrapiMedia } from "@/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";
import FloatingArrow from "../uiDecor/floatingArrow";
import { getBackgroundColor } from "@/utils/colorHelpers";

type ImageTextBannerWithLabelProps = {
    data: {
        id: number;
        heading: string;
        description: string;
        label: string;
        media: {
            url: string;
            alternativeText?: string;
        };
        waveBgColor: {
            waveBgColor: string;
        };
        backgroundColor: {
            backgroundColor: string;
        };
    };
};

export default function ImageTextBannerWithLabel({
    data,
}: ImageTextBannerWithLabelProps) {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const background = getBackgroundColor(data.backgroundColor?.backgroundColor);
    const isConclusion =
    data.label?.toLowerCase() === "conclusion";
    const sectionIdMap: Record<string, string> = {
        'Chapter 4': 'cdp-intro',
        'Chapter 5': 'cdp-case',
        'Conclusion': 'cdp-takeaway',
    }
    const sectionId = sectionIdMap[data.label]

    useGSAP(
        () => {
            gsap.to(imgRef.current, { opacity: 1, stagger: 0.3, y: 0 });
            gsap.to(".textChild", { opacity: 1, stagger: 0.3, y: 0 });
        },
        { scope: containerRef }
    );

    return (
        <section
            id= {sectionId}
            className={`bg-cover backdrop-invert relative px-8 sm:px-6 md:px-10 lg:px-[10vw] pt-16 ${
                isConclusion ? "pb-32 md:pb-44" : ""
            }`}
            style={{ background }}
        >
            <FloatingArrow
                direction="left"
                className="top-0 left-[10.625vw]"
                wrapText={data.label?.toLowerCase().replace(" ", "-")}
            />

            <div
                className="max-w-[93rem] mx-auto flex flex-col lg:flex-row md:gap-8 lg:gap-16 items-center pt-4 lg:pt-24"
                ref={containerRef}
            >
                <div className="w-full lg:w-1/2">
                    {data.label && (
                        <div className="flex items-center gap-2 mb-4 textChild opacity-0 translate-y-8">
                            <span className="w-3 h-3 bg-purple rounded-full"></span>
                            <span className="text-purple font-semibold text-sm md:text-base">
                                {data.label}
                            </span>
                        </div>
                    )}

                    {data.heading && (
                        <h2 className="textChild opacity-0 translate-y-8 text-gray-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-6 uppercase tracking-tight">
                            {data.heading}
                        </h2>
                    )}

                    {data.description && (
                        <div
                            className="textChild opacity-0 translate-y-8 text-gray-600 text-base md:text-lg lg:text-xl leading-relaxed space-y-4"
                            dangerouslySetInnerHTML={{
                                __html: data.description,
                            }}
                        />
                    )}
                </div>

                {data.media?.url && (
                    <div className="w-full lg:w-2/5">
                        <Image
                            src={getStrapiMedia(data.media.url)}
                            width={600}
                            height={500}
                            alt={
                                data.media.alternativeText || data.heading || ""
                            }
                            className="opacity-0 translate-y-8 w-full h-auto object-contain"
                            ref={imgRef}
                        />
                    </div>
                )}
            </div>

            {data.waveBgColor && (
                <BottomCurves
                    color={data.waveBgColor.waveBgColor}
                    direction="right"
                    className="z-1"
                />
            )}
        </section>
    );
}
